import { RRule, rrulestr, type Frequency, type Options, type RRuleSet } from "rrule";

import {
  INTERVAL_OPTIONS,
  WEEKDAYS,
} from "@/components/MaintenanceStrategyInterval/maintenanceStrategyIntervalOptions";
import {
  getDateIsBefore,
  getDateISO,
  getDateIsSameDay,
  getDateJS,
  getDateJSfromUTC,
  getDateMsDifference,
  getDatePlusMinus,
  getDateStartOfDay,
} from "@/utils/dates";
import { filterNotEmpty, getFirstArrayValue, isDefined } from "@/utils/format";

// a typical rule looks like this:
//  DTSTART:20240216T115818Z
//  RRULE:FREQ=MONTHLY;INTERVAL=1;WKST=MO
// note, that th DTSTART contains the time as well
// if we want to exclude a certain date, we need to make sure it uses the same time as DTSTART
export function alignDateToRRuleTime(date: Date, dtstart: Date): Date {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      dtstart.getUTCHours(),
      dtstart.getUTCMinutes(),
      dtstart.getUTCSeconds(),
    ),
  );
}

/**
 * @description Returns RRule in readable string format
 * @param rule string
 */
export function readableRRule(rule?: string | null): string | null {
  // TODO(cpannwitz): i18n for RRule, see this for inspiration: https://github.com/jakubroztocil/rrule/issues/13
  if (!rule) return null;
  return RRule.fromString(rule).toText();
}

/**
 * @description Returns next RRule occasion in readable string format
 * @param rule string
 */
export function nextRRuleOccasion(
  rule?: string | null,
  rruleStartDate?: string,
  exclude: string[] = [],
): string | null {
  if (!rule) return null;
  // forceset makes sure we get a RRuleSet back
  // we need to use `rrulestr` so it will parse DTSTART correctly
  // we need to use DTSTART because otherwise it will default to `new Date()`
  // which makes everything even more complicated to handle
  const rruleSet: RRuleSet = rrulestr(rule, { forceset: true }) as RRuleSet;
  const dtstart = getFirstArrayValue(rruleSet.rrules())?.options.dtstart;

  if (exclude.length > 0 && dtstart) {
    exclude.forEach((d) => rruleSet.exdate(alignDateToRRuleTime(getDateJSfromUTC(d), dtstart)));
  }

  const startDate =
    rruleStartDate && dtstart
      ? alignDateToRRuleTime(getDateJSfromUTC(rruleStartDate), dtstart)
      : getDateJSfromUTC(rruleStartDate);
  const next = rruleSet.after(startDate);
  if (!next) return null;
  return getDateISO(next);
}

/**
 * @description Returns previous RRule occasion in readable string format
 * @param rule string
 */
export function previousRRuleOccasion(
  rule?: string | null,
  rruleStartDate?: string,
  exclude: string[] = [],
): string | null {
  if (!rule) return null;
  const rruleSet: RRuleSet = rrulestr(rule, { forceset: true }) as RRuleSet;
  const dtstart = getFirstArrayValue(rruleSet.rrules())?.options.dtstart;

  if (exclude.length > 0 && dtstart) {
    exclude.forEach((d) => rruleSet.exdate(alignDateToRRuleTime(getDateJSfromUTC(d), dtstart)));
  }

  const startDate =
    rruleStartDate && dtstart
      ? alignDateToRRuleTime(getDateJSfromUTC(rruleStartDate), dtstart)
      : getDateJSfromUTC(rruleStartDate);
  const previous = rruleSet.before(startDate);
  if (!previous) return null;
  return getDateISO(previous);
}

/**
 * @description Aligns excluded dates to the rule's DTSTART time-of-day.
 * rrule matches `exdate` by exact timestamp, so an excluded date stored with a different
 * time (eg. the scheduler's ~02:00 run time) is silently ignored. Consumers that filter
 * by day do not need this; consumers that hand the dates to rrule do.
 */
export function alignExcludedDatesToRRule(interval: string, excludedDates: string[]): string[] {
  if (excludedDates.length === 0) return [];
  const dtstart = RRule.fromString(interval).origOptions.dtstart;
  if (!dtstart) return excludedDates;
  return excludedDates.map((d) => getDateISO(alignDateToRRuleTime(getDateJSfromUTC(d), dtstart)));
}

export function getRRuleDatesBetween(interval: string, startDate: string, endDate: string): string[] {
  return RRule.fromString(interval)
    .between(getDateJSfromUTC(startDate), getDateJSfromUTC(endDate), true)
    .map((date) => getDateISO(date));
}

export function getRRuleIntervalDurationMs(interval: string) {
  const rruleParsed = RRule.fromString(interval);
  const rruleNext2Dates = rruleParsed.all((_, i) => i < 2); // take next 2 dates
  return getDateMsDifference(rruleNext2Dates[1], rruleNext2Dates[0]);
}

export function isRruleDaily(interval: string) {
  return RRule.fromString(interval).options.freq === RRule.DAILY;
}

export function parseRRuleString(rrule: string) {
  const parsedRRule = RRule.fromString(rrule);

  const { freq, interval, byweekday, dtstart } = parsedRRule.origOptions;
  const startDate = dtstart ? getDateISO(dtstart) : null;

  const intervalOption = INTERVAL_OPTIONS.find(
    ({ frequency }) => frequency.freq === freq && frequency.interval === interval,
  );
  const byWeekDays = byweekday
    ? Array.isArray(byweekday)
      ? byweekday.map((weekday) => WEEKDAYS.find((w) => w.weekday === weekday)).filter(filterNotEmpty)
      : [WEEKDAYS.find((w) => w.weekday === byweekday)].filter(filterNotEmpty)
    : [];

  const rruleWithoutStartDate =
    isDefined(freq) && isDefined(interval) ? createRRuleString({ freq, interval, byweekday, startDate: null }) : "";

  return {
    intervalOption,
    byWeekDays,
    startDate,
    rrule,
    rruleWithoutStartDate,
  };
}

interface CreateRRuleStringProps {
  freq?: Frequency;
  interval?: number;
  byweekday?: Options["byweekday"];
  startDate?: string | null;
}
export function createRRuleString({ freq, interval, byweekday, startDate }: CreateRRuleStringProps) {
  const dtstart = startDate ? getDateJS(startDate) : null;
  return new RRule(
    {
      freq,
      interval,
      dtstart,
      byweekday,
      wkst: RRule.MO,
    },
    true,
  ).toString();
}

export function setRruleStartDate(rrule: string, startDate?: string) {
  const parsedRRule = RRule.fromString(rrule);
  const { freq, interval, byweekday, dtstart } = parsedRRule.origOptions;
  const newStartDate = startDate ?? (dtstart ?? new Date()).toISOString();

  return createRRuleString({ freq: freq, interval, byweekday, startDate: newStartDate });
}

interface GetRruleDivergationDatesProps {
  interval: string;
  lastDate?: string | null;
  nextDate?: string | null;
}
export function getRruleDivergationDates({ interval, lastDate, nextDate }: GetRruleDivergationDatesProps) {
  const nextOccurence = nextRRuleOccasion(interval);
  if (!lastDate || !nextOccurence) return null;

  const scheduleIntervalDurationMs = getRRuleIntervalDurationMs(interval);
  const lastDateCompareDate = getDatePlusMinus(lastDate, scheduleIntervalDurationMs, "milliseconds");
  const isNextDateBeforeNextOccurence = nextDate ? getDateIsBefore(nextDate, nextOccurence) : false;
  const actualNextOccurenceDate = isNextDateBeforeNextOccurence ? nextDate : nextOccurence;

  const oldNextOccurenceDate = nextRRuleOccasion(interval);
  let updatedScheduleInterval: string | null = null;
  let newNextOccurenceDate: string | null = null;

  if (nextDate && isNextDateBeforeNextOccurence) {
    // last date + interval(original) !== next date ? -> new interval start date = next maintenance - interval(last date -> next date)
    const intervalDatesDivergating = !getDateIsSameDay(lastDateCompareDate, nextDate);

    if (intervalDatesDivergating) {
      const divergationInterval = getDateMsDifference(getDateStartOfDay(lastDate), getDateStartOfDay(nextDate));
      const newIntervalStartDate = getDatePlusMinus(nextDate, -divergationInterval, "milliseconds");

      updatedScheduleInterval = setRruleStartDate(interval, getDateStartOfDay(newIntervalStartDate));

      newNextOccurenceDate = nextDate;
    }
  } else {
    // last date + interval(original) !== next date ? -> new interval start date = last date + interval(original)
    const intervalDatesDivergating = !getDateIsSameDay(lastDateCompareDate, nextOccurence);

    if (intervalDatesDivergating) {
      const newIntervalStartDate = lastDateCompareDate;

      updatedScheduleInterval = setRruleStartDate(interval, getDateStartOfDay(newIntervalStartDate));

      newNextOccurenceDate = nextRRuleOccasion(updatedScheduleInterval);
    }
  }

  return {
    updatedScheduleInterval,
    actualNextOccurenceDate,
    oldNextOccurenceDate,
    newNextOccurenceDate,
  };
}
